import { create } from 'zustand';
import type { ViewerNavigationMode } from '@mindcloudxai_3d/core';
import type { SceneConfig, TaskData, PublishStatus, SceneRole, AccessInfo } from '@app-types/scene';

export type LoadStatus = 'idle' | 'loading' | 'ready' | 'error' | 'not-found';
export type NavigationMode = ViewerNavigationMode;
export type CinematicState = 'idle' | 'playing' | 'paused';
export type MeasureMode = 'idle' | 'placing';
export type UnitSystem = 'metric' | 'imperial';

interface ViewerActions {
  setSceneId: (id: string | null) => void;
  setStatus: (status: LoadStatus, errorMessage?: string) => void;
  setLoadProgress: (progress: number | null) => void;
  setTaskData: (data: TaskData) => void;
  setSceneConfig: (config: SceneConfig) => void;
  setNavigationMode: (mode: NavigationMode) => void;
  setCinematicState: (state: CinematicState) => void;
  setCinematicCurrentTime: (t: number) => void;
  setActiveHotspotIndex: (index: number | null) => void;
  setHotspotPanelOpen: (open: boolean) => void;
  setMeasureMode: (mode: MeasureMode) => void;
  setUnitSystem: (system: UnitSystem) => void;
  setSnapshotBusy: (busy: boolean) => void;
  requestSnapshot: () => void;
  setSharePanelOpen: (open: boolean) => void;
  setSceneRole: (role: SceneRole) => void;
  setPublishStatus: (status: PublishStatus) => void;
  setShortUrl: (url: string | null) => void;
  setAccessInfo: (info: AccessInfo | null) => void;
  setSceneInfoOpen: (open: boolean) => void;
  setImmersive: (immersive: boolean) => void;
  reset: () => void;
}

interface ViewerState extends ViewerActions {
  sceneId: string | null;
  status: LoadStatus;
  /** Load progress 0–100; null means indeterminate. */
  loadProgress: number | null;
  errorMessage: string | null;
  /** Raw task data from the backend. */
  taskData: TaskData | null;
  /** Processed scene config for the renderer. */
  sceneConfig: SceneConfig | null;

  // Navigation
  navigationMode: NavigationMode;

  // Cinematic
  cinematicState: CinematicState;
  cinematicCurrentTime: number; // seconds

  // Hotspots
  activeHotspotIndex: number | null;
  hotspotPanelOpen: boolean;

  // Tools
  measureMode: MeasureMode;
  unitSystem: UnitSystem;
  snapshotBusy: boolean;
  snapshotRequestId: number;
  sharePanelOpen: boolean;

  // 分享与权限
  sceneRole: SceneRole;
  publishStatus: PublishStatus;
  shortUrl: string | null;
  /** /task/visit 或 /share/visit 返回的访问权限信息 */
  accessInfo: AccessInfo | null;

  // UI
  sceneInfoOpen: boolean;
  /** 沉浸模式：pointer-lock 或移动端空闲时隐藏 HUD */
  immersive: boolean;
}

function readUnitSystem(): UnitSystem {
  try {
    return localStorage.getItem('user_setting_unit_system') === 'metric' ? 'metric' : 'imperial';
  } catch {
    return 'imperial';
  }
}

const initial: Omit<ViewerState, keyof ViewerActions> = {
  sceneId: null,
  status: 'idle',
  loadProgress: null,
  errorMessage: null,
  taskData: null,
  sceneConfig: null,
  navigationMode: 'orbit',
  cinematicState: 'idle',
  cinematicCurrentTime: 0,
  activeHotspotIndex: null,
  hotspotPanelOpen: false,
  measureMode: 'idle',
  unitSystem: readUnitSystem(),
  snapshotBusy: false,
  snapshotRequestId: 0,
  sharePanelOpen: false,
  sceneRole: 'anonymous',
  publishStatus: 'published',
  shortUrl: null,
  accessInfo: null,
  sceneInfoOpen: false,
  immersive: false,
};

export const useViewerStore = create<ViewerState>((set) => ({
  ...initial,
  setSceneId: (sceneId) => set({ sceneId }),
  setStatus: (status, errorMessage) =>
    set({ status, errorMessage: status === 'loading' ? null : (errorMessage ?? null) }),
  setLoadProgress: (loadProgress) => set({ loadProgress }),
  setTaskData: (taskData) => set({ taskData }),
  setSceneConfig: (sceneConfig) => set({ sceneConfig }),
  setNavigationMode: (navigationMode) => set({ navigationMode }),
  setCinematicState: (cinematicState) => set({ cinematicState }),
  setCinematicCurrentTime: (cinematicCurrentTime) => set({ cinematicCurrentTime }),
  setActiveHotspotIndex: (activeHotspotIndex) => set({ activeHotspotIndex }),
  setHotspotPanelOpen: (hotspotPanelOpen) => set({ hotspotPanelOpen }),
  setMeasureMode: (measureMode) => set({ measureMode }),
  setUnitSystem: (system) => set({ unitSystem: system === 'imperial' ? 'imperial' : 'metric' }),
  setSnapshotBusy: (snapshotBusy) => set({ snapshotBusy }),
  requestSnapshot: () => set((state) => ({ snapshotRequestId: state.snapshotRequestId + 1 })),
  setSharePanelOpen: (sharePanelOpen) => set({ sharePanelOpen }),
  setSceneRole: (sceneRole) => set({ sceneRole }),
  setPublishStatus: (publishStatus) => set({ publishStatus }),
  setShortUrl: (shortUrl) => set({ shortUrl }),
  setAccessInfo: (accessInfo) => set({ accessInfo }),
  setSceneInfoOpen: (sceneInfoOpen) => set({ sceneInfoOpen }),
  setImmersive: (immersive) => set({ immersive }),
  reset: () =>
    set({
      ...initial,
      unitSystem: readUnitSystem(),
    }),
}));
